import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import styles from "./Custombarchart.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

/* Read a CSS custom-property from :root */
const cssVar = (name) => {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
};

/* ── Regular (non-stacked) colour palette ── */
const COLOR_PALETTE = [
  { darkVar: "--violet-dark", lightVar: "--violet-light" },
  { darkVar: "--green-dark", lightVar: "--green-light" },
  { darkVar: "--pink-dark", lightVar: "--pink-light" },
  { darkVar: "--orange-dark", lightVar: "--orange-light" },
  { darkVar: "--lime-dark", lightVar: "--lime-light" },
  { darkVar: "--red-dark", lightVar: "--red-light" },
];

/* ── Strong / Weak stacked segments ── */
const DEFAULT_SEGMENTS = [
  { key: "strong", darkVar: "--green-dark", lightVar: "--green-light" },
  { key: "weak", darkVar: "--red-dark", lightVar: "--red-light" },
];

const getColorPair = (index) => {
  const len = COLOR_PALETTE.length;
  const numeric = Number.isFinite(index) ? index : 0;
  const safeIdx = ((numeric % len) + len) % len;
  const p = COLOR_PALETTE[safeIdx] || COLOR_PALETTE[0];
  return { dark: cssVar(p.darkVar), light: cssVar(p.lightVar) };
};

/* Full-chart-area gradient for regular bars */
const buildGradient = (canvasCtx, chartArea, idx) => {
  const { dark, light } = getColorPair(idx);
  const g = canvasCtx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom,
  );
  g.addColorStop(0, dark);
  g.addColorStop(1, light);
  return g;
};

/* Per-bar gradient for stacked segments.
 * Always derive the gradient from the (stable) chartArea instead of the
 * per-element bar.y / bar.base. Those are unreliable at option-resolution
 * time: freshly created bars (e.g. after drill-down → back) are initialised
 * at the baseline (y === base), so the old code collapsed to a flat colour. */
const buildPerBarGradient = (chart, ctx, colorCfg) => {
  const { ctx: canvasCtx, chartArea } = chart;
  const dark = cssVar(colorCfg.darkVar);
  const light = cssVar(colorCfg.lightVar);

  // Layout not ready on the very first paint — return solid and let
  // Chart.js re-resolve once chartArea exists.
  if (!chartArea) return dark;

  const g = canvasCtx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom,
  );
  g.addColorStop(0, dark);
  g.addColorStop(1, light);
  return g;
};

/* Compact axis labels once values run into thousands/lakhs/crores,
 * e.g. 4000 -> "4K", 125000 -> "1.25L", 12000000 -> "1.2Cr". */
const formatCompactAxisValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "0";
  if (num >= 10000000)
    return `${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 2)}Cr`;
  if (num >= 100000)
    return `${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 2)}L`;
  if (num >= 1000) return `${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}K`;
  return num.toString();
};

/* Draws value labels centred inside each stacked segment.
 * `suffix` lets the same chart show "%" (percentages) or "" (raw counts). */
const makeValueLabelPlugin = (suffix = "%") => ({
  id: "stackedValueLabels",
  afterDatasetsDraw(chart) {
    const { ctx: c } = chart;
    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) return;
      meta.data.forEach((bar, bi) => {
        const val = ds.data[bi];
        if (!val) return; // skip empty/zero segments
        const segH = Math.abs(bar.base - bar.y);
        if (segH < 14) return; // skip segments too short to label
        c.save();
        c.fillStyle = "#1f2937";
        c.font = "600 12px -apple-system,BlinkMacSystemFont,sans-serif";
        c.textAlign = "center";
        c.textBaseline = "middle";
        c.fillText(`${val}${suffix}`, bar.x, (bar.y + bar.base) / 2);
        c.restore();
      });
    });
  },
});

const CustomBarChart = ({
  icon = null,
  title = "Performance Overview",
  chartTitle = "",
  datasetLabel = "Dataset 1",
  valueLabel = "Exam Percentage",
  data = [],
  loading = false,
  error = null,
  maxY = 100,
  stepSize = 10,
  /* Suffix shown on bar value labels + tooltips + y-axis ticks. "%" for
   * percentages, "" for raw counts (fail count / remedial count, etc.). */
  valueSuffix = "%",
  /* Optional y-axis title, e.g. "Percentage(%)" or "Count". Empty → no title. */
  yAxisLabel = "",
  /* Opt-in: render y-axis ticks as 4K / 1.25L / 1.2Cr once values run into
   * the thousands. Off by default so percentage/count charts elsewhere
   * keep their plain-number ticks. */
  compactYAxis = false,
  /* Optional extra tooltip line(s) appended AFTER the segment values (e.g. an
   * FTR row). Receives the hovered category index → string | string[] | null.
   * Rendered with no colour swatch, so it reads as a summary line. */
  tooltipAfterBody = null,
  onBarClick,
  bottomHint = false,
  stacked = false,
  drilldown = false,
  serverDrilldown = false,
  /* Levels at which a click should bubble up in serverDrilldown mode.
   * Default keeps the legacy 2-level (class → section) behaviour; pass
   * ["class","section","subject"] to enable a 3rd drill step. */
  serverDrillLevels = ["class", "section"],
  level = "class",
  onBack,
  segments = DEFAULT_SEGMENTS,
  segmentLabels = { strong: "Strong", weak: "Weak" },
  sectionWiseSuffix = " - Section Wise",
  subjectWiseSuffix = " - Subject Wise",
  classHint = "Click any class to view section-wise breakdown",
  sectionHint = "Click any section to view subject-wise breakdown",
  subjectHint = "",
  backHint = "click X to go back",
  info = "",
  onViewAll,
  viewAllLabel = "View All",
}) => {
  /* non-stacked: exam → subject drill-down */
  const [selectedExam, setSelectedExam] = useState(null);
  /* stacked (INTERNAL): class → section → subject drill-down */
  const [drillLevel, setDrillLevel] = useState("class");
  const [classIdx, setClassIdx] = useState(null);
  const [sectionIdx, setSectionIdx] = useState(null);

  /* Value label plugin re-built only when the suffix changes. */
  const valueLabelPlugin = useMemo(
    () => makeValueLabelPlugin(valueSuffix),
    [valueSuffix],
  );

  /* Active level: parent-controlled in server mode, internal otherwise */
  const lvl = serverDrilldown ? level : drillLevel;

  let chartData;
  let chartOptions;
  let extraPlugins = [];
  let headerTitle = title;
  let hint = "";
  let showClose = false;

  if (stacked) {
    /* ── Resolve rows for current drill level ── */
    let rows = data;
    if (
      !serverDrilldown &&
      drilldown &&
      drillLevel === "section" &&
      classIdx != null
    ) {
      rows = data[classIdx]?.sections || [];
    } else if (
      !serverDrilldown &&
      drilldown &&
      drillLevel === "subject" &&
      classIdx != null &&
      sectionIdx != null
    ) {
      rows = data[classIdx]?.sections?.[sectionIdx]?.subjects || [];
    }
    /* serverDrilldown → rows = data (parent already supplied current level) */

    const segDefs = segments.map((s) => ({
      ...s,
      label: segmentLabels[s.key] ?? s.key,
    }));

    chartData = {
      labels: rows.map((r) => r.label),
      datasets: segDefs.map((seg, si) => ({
        label: seg.label,
        data: rows.map((r) => r[seg.key] ?? 0),
        backgroundColor: (ctx) => buildPerBarGradient(ctx.chart, ctx, seg),
        hoverBackgroundColor: (ctx) => buildPerBarGradient(ctx.chart, ctx, seg),
        borderWidth: 0,
        borderRadius:
          si === segDefs.length - 1
            ? { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 }
            : 0,
        borderSkipped: false,
        barPercentage: 0.55,
        categoryPercentage: 0.7,
      })),
    };

    const canDrill = serverDrilldown
      ? serverDrillLevels.includes(lvl)
      : drilldown && (drillLevel === "class" || drillLevel === "section");

    chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      // Index mode → hovering any segment of a bar shows BOTH segments
      // (e.g. Strong + Weak, or Fail Count + Remedial Count) in one tooltip.
      interaction: { mode: "index", intersect: false },
      onClick: (_evt, elements) => {
        if (!elements?.length) return;
        const idx = elements[0].index;

        /* ── SERVER-DRIVEN: just bubble the clicked datum up ── */
        if (serverDrilldown) {
          if (serverDrillLevels.includes(lvl)) {
            if (typeof onBarClick === "function")
              onBarClick(rows[idx], idx, lvl);
          }
          return;
        }

        /* ── INTERNAL nested drilldown ── */
        if (!drilldown) return;
        if (drillLevel === "class") {
          const cls = data[idx];
          if (cls?.sections?.length) {
            setClassIdx(idx);
            setSectionIdx(null);
            setDrillLevel("section");
            if (typeof onBarClick === "function") onBarClick("section", cls);
          }
        } else if (drillLevel === "section") {
          const secs = data[classIdx]?.sections || [];
          if (secs[idx]?.subjects?.length) {
            setSectionIdx(idx);
            setDrillLevel("subject");
            if (typeof onBarClick === "function")
              onBarClick("subject", secs[idx]);
          }
        }
      },
      onHover: (evt, elements) => {
        const canvas = evt?.native?.target;
        if (canvas) {
          canvas.style.cursor =
            canDrill && elements.length ? "pointer" : "default";
        }
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { color: "#374151", font: { size: 11, weight: "500" } },
          border: { display: false },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          // Dynamic axis: caller passes maxY/stepSize sized to the data
          // (e.g. counts up to 1000 → max 1000, step 200).
          max: maxY,
          ticks: {
            stepSize,
            color: "#6B7280",
            // Append the suffix → "100%" for percentages, plain number for counts.
            callback: (value) => `${value}${valueSuffix}`,
          },
          grid: { color: "#E5E7EB" },
          border: { display: false },
          title: {
            display: !!yAxisLabel,
            text: yAxisLabel,
            color: "#6b7280",
            font: { size: 13, weight: "600" },
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          align: "center",
          labels: {
            usePointStyle: true,
            pointStyle: "rect",
            boxWidth: 14,
            boxHeight: 10,
            padding: 16,
            font: { size: 12, weight: "500" },
            color: "#4b5563",
            filter: (legendItem) => legendItem.text?.trim() !== "",
          },
        },
        tooltip: {
          // Show every dataset (segment) that shares the hovered category.
          mode: "index",
          intersect: false,
          backgroundColor: "#1F2937",
          padding: 12,
          cornerRadius: 8,
          titleFont: { weight: "bold" },
          callbacks: {
            label: (ctx) =>
              ctx.dataset.label?.trim()
                ? ` ${ctx.dataset.label}: ${ctx.parsed.y}${valueSuffix}`
                : null,
            // Append summary line(s) (e.g. "FTR: 0.74") after the segments.
            afterBody: (items) => {
              if (typeof tooltipAfterBody !== "function" || !items?.length)
                return [];
              const out = tooltipAfterBody(items[0].dataIndex);
              if (out == null) return [];
              return Array.isArray(out) ? out : [out];
            },
          },
        },
      },
    };

    extraPlugins = [valueLabelPlugin];

    /* ── Header title + close button per level (both drill modes) ── */
    if (serverDrilldown || drilldown) {
      if (lvl === "section") {
        headerTitle = `${title}${sectionWiseSuffix}`;
        showClose = true;
      } else if (lvl === "subject") {
        headerTitle = `${title}${subjectWiseSuffix}`;
        showClose = true;
      }
    }

    if (bottomHint) {
      // Join a level hint with the back hint, skipping empties so we never
      // emit a stray " - " or a lone back hint when a level hint is cleared.
      const withBack = (base) =>
        base && backHint ? `${base} - ${backHint}` : base || backHint || "";
      if (serverDrilldown || drilldown) {
        if (lvl === "class") {
          hint = classHint;
        } else if (lvl === "section") {
          hint = withBack(sectionHint);
        } else if (lvl === "subject") {
          hint = withBack(subjectHint);
        }
      } else {
        hint = classHint;
      }
    }
  } else {
    /* ───────────── Non-stacked bar (exam → subject drill-down) ───────────── */
    const isDrillDown = selectedExam !== null;
    const rawSelectedIndex = isDrillDown
      ? data.findIndex((d) => d.exam_name === selectedExam)
      : -1;
    const selectedExamIndex =
      isDrillDown && rawSelectedIndex < 0 ? 0 : rawSelectedIndex;

    let labels = [];
    let values = [];

    if (isDrillDown) {
      const exam = data.find((d) => d.exam_name === selectedExam);
      labels = exam?.subjects?.map((s) => s.name) || [];
      values = exam?.subjects?.map((s) => s.mark) || [];
    } else {
      labels = data.map((d) => d.exam_name);
      values = data.map((d) => d.overAllMark);
    }

    chartData = {
      labels,
      datasets: [
        {
          label: datasetLabel,
          data: values,
          backgroundColor: (ctx) => {
            const chart = ctx.chart;
            const { ctx: canvasCtx, chartArea } = chart;
            if (!chartArea) return cssVar(getColorPair(0).dark);
            const colorIndex = isDrillDown ? selectedExamIndex : ctx.dataIndex;
            return buildGradient(canvasCtx, chartArea, colorIndex);
          },
          borderColor: (ctx) => {
            const colorIndex = isDrillDown ? selectedExamIndex : ctx.dataIndex;
            return getColorPair(colorIndex).dark;
          },
          borderWidth: 1,
          borderRadius: 8,
          barPercentage: 0.55,
          categoryPercentage: 0.7,
        },
      ],
    };

    chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: { display: !!chartTitle, text: chartTitle },
        tooltip: {
          backgroundColor: "#1F2937",
          padding: 12,
          cornerRadius: 8,
          titleFont: { weight: "bold" },
          callbacks: {
            label: (ctx) => `${valueLabel}: ${ctx.parsed.y}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: maxY,
          ticks: {
            stepSize,
            color: "#6B7280",
            ...(compactYAxis && {
              callback: (value) => formatCompactAxisValue(value),
            }),
          },
          grid: { color: "#E5E7EB" },
        },
        x: {
          ticks: { color: "#374151", font: { weight: "500" } },
          grid: { display: false },
        },
      },
      onClick: (_event, elements) => {
        if (isDrillDown) return;
        if (elements?.length > 0) {
          const idx = elements[0].index;
          const clicked = data[idx];
          if (clicked?.subjects?.length > 0) {
            setSelectedExam(clicked.exam_name);
            if (typeof onBarClick === "function") onBarClick(clicked.exam_name);
          }
        }
      },
    };

    headerTitle = isDrillDown
      ? `${title} - ${selectedExam} (Subject-wise)`
      : title;
    showClose = isDrillDown;
    if (bottomHint) {
      hint = isDrillDown ? "Subject-wise breakdown - click X to go back" : "  ";
    }
  }

  /* ── Step one level back ── */
  const handleClose = () => {
    if (serverDrilldown) {
      if (typeof onBack === "function") onBack();
      return;
    }
    if (stacked) {
      if (drillLevel === "subject") {
        setDrillLevel("section");
        setSectionIdx(null);
      } else if (drillLevel === "section") {
        setDrillLevel("class");
        setClassIdx(null);
      }
    } else {
      setSelectedExam(null);
    }
  };

  /* ── Loading / Error overlay ── */
  const renderOverlay = () => {
    if (loading) {
      return (
        <div className={styles.overlay}>
          <span className={styles.overlayText}>
            <span className={styles.spinner} />
            Loading…
          </span>
        </div>
      );
    }
    if (error) {
      return (
        <div className={styles.overlay}>
          <span className={styles.overlayText}>{error}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-100">
      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {icon && (
            <span className={styles.icon} aria-hidden="true">
              {icon}
            </span>
          )}
          <h3 className="chartTitle">{headerTitle}</h3>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {info && (
            <div className={styles.infoNote}>
              {" "}
              <strong>Note:</strong> {info}
            </div>
          )}
          {stacked && onViewAll && lvl === "class" && (
            <button
              type="button"
              onClick={onViewAll}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                color: cssVar("--orange-dark"),
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {viewAllLabel}
            </button>
          )}
          {showClose && (
            <button
              type="button"
              onClick={handleClose}
              aria-label="Close drill-down view"
              className={styles.closeBtn}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* ── Chart (with overlay for loading/error/empty) ── */}
      <div className={styles.chartContainer} style={{ position: "relative" }}>
        {renderOverlay()}
        <Bar data={chartData} options={chartOptions} plugins={extraPlugins} />
      </div>

      {/* ── Hint — rendered BELOW the legend (which is inside the canvas) ── */}
      {hint && <div className={styles.viewHint}>{hint}</div>}
    </div>
  );
};

export default CustomBarChart;
